<script setup lang="ts">
import type { AddonOpportunity, Addons, ParsedInstallment, SimulationOpportunities } from '~/utils/simulation/Simulation'
import IMaterialSymbolsWorkspacePremiumOutline from '~icons/material-symbols/workspace-premium-outline'
import { useInsuranceStorage } from '~/composables/useInsuranceStorage'
import useAuth from '~/store-v2/auth/useAuth'
import { useAddonStore } from '~/store/addons'

const props = defineProps<{
  projectIsCompleted?: boolean
  projectAddons?: Addons[]
  projectSelectedInstallmentsAddons?: Addons[]
  installment?: ParsedInstallment | null
  projectId?: string
}>()

const emit = defineEmits<{
  insuranceToggled: [{ insuranceType: string, isNowApplied: boolean }]
}>()

onBeforeMount(async () => {
  await simulationOpportunities()
})

const responseSimulationOpportunities = ref<SimulationOpportunities>()
const pointsInsuranceEquipment = ref<AddonOpportunity[]>()
const pointsInsuranceFinancialProtection = ref<AddonOpportunity[]>()
const { getSimulationOpportunities } = useResumeStore()
const { toggleInsurance, addons_list } = useAddonStore()
const { data: auth } = useAuth()
const { saveInsuranceDecision } = useInsuranceStorage()

const userBlack = auth.value?.person.partner?.sfplus?.is_level_black

const { formatMoney } = useMoney()

const insuranceStates = ref<Record<string, boolean>>({})

function initializeInsuranceStates() {
  const insuranceSlugs = ['Riscos Diversos', 'Prestamista']

  insuranceSlugs.forEach((slug) => {
    const selectedInstallmentAddon = props.projectSelectedInstallmentsAddons?.find(value => value.product_slug === slug)
    if (selectedInstallmentAddon) {
      insuranceStates.value[slug] = selectedInstallmentAddon.applied || false
      return
    }

    const projectAddon = props.projectAddons?.find(value => value.product_slug === slug)
    if (projectAddon) {
      insuranceStates.value[slug] = projectAddon.applied || false
      return
    }

    const storeAddon = addons_list.find(value => value.product_slug === slug)
    insuranceStates.value[slug] = storeAddon?.applied || false
  })
}

onMounted(() => {
  simulationOpportunities()
  initializeInsuranceStates()
})

watch(() => [props.projectSelectedInstallmentsAddons, props.projectAddons], () => {
  simulationOpportunities()
  initializeInsuranceStates()
}, { deep: true, immediate: true })

watch(addons_list, () => {
  initializeInsuranceStates()
}, { deep: true })

function toggleAddonAdapter(value: any) {
  const insuranceSlug = typeof value === 'string' ? value : value?.target?.value || value?.value

  if (!insuranceSlug) {
    return
  }

  const insuranceType = insuranceSlug as 'Riscos Diversos' | 'Prestamista'

  if (getIsLocked(insuranceType)) {
    return
  }

  const wasApplied = getIsApplied(insuranceType)
  const isNowApplied = !wasApplied

  insuranceStates.value[insuranceType] = isNowApplied

  toggleInsurance(insuranceSlug)

  if (props.projectId && !props.projectIsCompleted) {
    saveInsuranceDecision(props.projectId, insuranceType, isNowApplied)
  }

  nextTick(() => {
    emit('insuranceToggled', { insuranceType, isNowApplied })
  })
}

function getLabel(addonName: string) {
  const isApplied = getIsApplied(addonName)
  return isApplied ? 'Adicionado' : 'Adicionar'
}

function getIsApplied(addonName: string): boolean {
  if (insuranceStates.value[addonName] !== undefined) {
    return insuranceStates.value[addonName]
  }

  const selectedInstallmentAddon = props.projectSelectedInstallmentsAddons?.find(value => value.product_slug === addonName)
  if (selectedInstallmentAddon) {
    return selectedInstallmentAddon.applied || false
  }

  const projectAddon = props.projectAddons?.find(value => value.product_slug === addonName)
  if (projectAddon) {
    return projectAddon.applied || false
  }

  const storeAddon = addons_list.find(value => value.product_slug === addonName)
  return storeAddon?.applied || false
}

function getIsLocked(addonName: string): boolean {
  const selectedInstallmentAddon = props.projectSelectedInstallmentsAddons?.find(value => value.product_slug === addonName)
  if (selectedInstallmentAddon) {
    return selectedInstallmentAddon.locked || false
  }

  const projectAddon = props.projectAddons?.find(value => value.product_slug === addonName)
  if (projectAddon) {
    return projectAddon.locked || false
  }

  const storeAddon = addons_list.find(value => value.product_slug === addonName)
  return storeAddon?.locked || false
}

const verifyInsuranceIsAvailable = computed(() => {
  const hasInsuranceInStore = addons_list.find(e => e.product_slug === 'Prestamista')
  const hasInsuranceInProject = props.projectAddons?.find(e => e.product_slug === 'Prestamista')
  const hasInsuranceInSelected = props.projectSelectedInstallmentsAddons?.find(e => e.product_slug === 'Prestamista')

  return hasInsuranceInStore || hasInsuranceInProject || hasInsuranceInSelected
})

const hasEquipmentInsurance = computed(() => {
  const hasInsuranceInStore = addons_list.find(item => item.product_slug === 'Riscos Diversos')
  const hasInsuranceInProject = props.projectAddons?.find(e => e.product_slug === 'Riscos Diversos')
  const hasInsuranceInSelected = props.projectSelectedInstallmentsAddons?.find(e => e.product_slug === 'Riscos Diversos')

  return !!(hasInsuranceInStore || hasInsuranceInProject || hasInsuranceInSelected)
})

const verifyEquipmentInsurance = computed(() => {
  const canShow = !userBlack || !props.installment?.combo

  return hasEquipmentInsurance.value && canShow
})

const showBlackFreeEquipmentInsurance = computed(() => userBlack && props.installment?.combo && hasEquipmentInsurance.value)

function getFieldValueOfAddon(addon: string, field: keyof Addons) {
  const projectAddon = props.projectSelectedInstallmentsAddons?.find(value => value.product_slug === addon)
  if (projectAddon) {
    const value = Math.abs(Number(projectAddon[field]))
    if (value > 0) return value
  }

  const storeAddon = addons_list.find(value => value.product_slug === addon)
  if (storeAddon) {
    const value = Math.abs(Number(storeAddon[field]))
    if (value > 0) return value
  }

  const generalProjectAddon = props.projectAddons?.find(value => value.product_slug === addon)
  if (generalProjectAddon) {
    const value = Math.abs(Number(generalProjectAddon[field]))
    if (value > 0) return value
  }

  return 0
}

async function simulationOpportunities() {
  responseSimulationOpportunities.value = getSimulationOpportunities()
  pointsInsuranceFinancialProtection.value = responseSimulationOpportunities?.value?.addons_opportunities.filter(opportunity => opportunity.product_slug === 'Riscos Diversos')
  pointsInsuranceEquipment.value = responseSimulationOpportunities?.value?.addons_opportunities.filter(opportunity => opportunity.product_slug === 'Prestamista')
}
</script>

<template>
  <div class="col-span-5" :class="projectIsCompleted ? 'cursor-not-allowed' : ''">
    <div class="details-container">
      <h2 class="text-2xs font-bold lg:site:mb-8">
        Seguros Solfácil
      </h2>
      <SolAlert v-if="!verifyInsuranceIsAvailable && !projectIsCompleted" id="informative_insurance" feedback="informative" title="Seguro contra perda de renda" class="lg:site:mb-8">
        Devido às nossas políticas, este cliente infelizmente não é elegível para este seguro.
      </SolAlert>

      <!-- Seguro contra imprevistos no equipamento -->
      <div v-if="verifyEquipmentInsurance" class="container-benefits-details">
        <div class="benefits-details-title">
          <img class="benefits-tag" src="/icons/insurance.svg">
          <div class="mb:site:w-[240px]">
            <h3 class="pb-2 flex flex-col lg:site:gap-1">
              <span class="fonts-body-large-bold benefits-title !text-3xs !md:site:mb-0">Seguro do equipamento solar</span>
              <span v-if="responseSimulationOpportunities && pointsInsuranceFinancialProtection" class="text-brand-secondary-dark flex items-center text-micro">
                <IMaterialSymbolsWorkspacePremiumOutline class="w-[16px] h-[16px] inline mr-1.5" />
                <span><strong>+{{ pointsInsuranceFinancialProtection[0].gained_points.toLocaleString('pt-BR') }} pontos </strong> no Solfácil Mais</span>
              </span>
            </h3>
            <p class="fonts-body-medium-regular text-brand-primary-medium">
              Proteja seu cliente contra imprevistos em equipamentos já instalados, como incêndios, danos elétricos, furtos e roubos.
              <a
                :href="projectIsCompleted ? '#' : 'https://solfacil.com.br/seguros/?_gl=1*am5k6q*_ga*ODYzMTgzMjkyLjE2ODAwOTkxMTQ.*_ga_NQJ9ZNTL71*MTY4NDkzNjA2Mi40Ny4xLjE2ODQ5MzYwNjkuMC4wLjA'"
                class="underline fonts-body-medium-regular"
                :class="projectIsCompleted ? 'text-brand-primary-medium opacity-75 cursor-not-allowed' : 'text-brand-secondary-pure'"
                target="_blank"
              >
                Mais detalhes
              </a>
            </p>
          </div>
        </div>
        <div class="benefits-details-elements">
          <div class="min-w-[142px] flex flex-col gap-1">
            <h3 class="text-[16px] benefits-title text-neutral-low-medium">
              <strong class="!text-[16px] fonts-body-medium-bold">{{ `+ ${formatMoney(getFieldValueOfAddon('Riscos Diversos', 'installment_price'))}` }}</strong><span class="text-[14px]">/mês</span>
            </h3>
            <p class="!text-[12px] text-neutral-low-light">
              <span class="!text-[12px] fonts-body-medium-bold">Sua comissão: </span><span>{{ formatMoney(getFieldValueOfAddon('Riscos Diversos', 'partner_commission')) }}</span>
            </p>
          </div>

          <SolSwitch
            id="Riscos Diversos"
            data-testid="equipament-insurance"
            :label="getLabel('Riscos Diversos')"
            text-direction="right"
            :disabled="projectIsCompleted || getIsLocked('Riscos Diversos')"
            :checked="getIsApplied('Riscos Diversos')"
            name="Riscos Diversos"
            value="Riscos Diversos"
            @change="toggleAddonAdapter"
          />
        </div>
      </div>

      <!-- Solfacil mais black -->
      <div v-if="showBlackFreeEquipmentInsurance" class="container-benefits-details">
        <div class="benefits-details-title ">
          <img class="benefits-tag" src="/icons/insurance.svg">
          <div class="mb:site:w-[240px]">
            <h3 class="fonts-body-large-bold benefits-title !text-3xs">
              Seguro do equipamento solar
            </h3>
            <p class="fonts-body-medium-regular text-brand-primary-medium">
              Proteja seu cliente contra imprevistos em equipamentos já instalados, como incêndios, danos elétricos, furtos e roubos.
              <a
                :href="projectIsCompleted ? '#' : 'https://solfacil.com.br/seguros/?_gl=1*am5k6q*_ga*ODYzMTgzMjkyLjE2ODAwOTkxMTQ.*_ga_NQJ9ZNTL71*MTY4NDkzNjA2Mi40Ny4xLjE2ODQ5MzYwNjkuMC4wLjA'"
                class="underline fonts-body-medium-regular"
                :class="projectIsCompleted ? 'text-brand-primary-medium opacity-75 cursor-not-allowed' : 'text-brand-secondary-pure'"
                target="_blank"
              >
                Mais detalhes
              </a>
            </p>
          </div>
        </div>
        <div class="benefits-details-elements">
          <div class="min-w-[142px] flex flex-col gap-1">
            <h3 class="text-[16px] benefits-title text-neutral-low-medium">
              <strong class="!text-[16px] fonts-body-medium-bold">Grátis</strong>
            </h3>
            <p class="!text-[12px] text-neutral-low-light">
              <span>No Combo Fácil</span>
            </p>
          </div>
          <SolSwitch
            id="SolfacilMaisBlack"
            label="Adicionado"
            text-direction="right"
            :disabled="true"
            :checked="true"
            name="SolfacilMaisBlack"
            value="SolfacilMaisBlack"
          />
        </div>
      </div>
      <!-- Close Solfacil mais black -->
      <!-- Close Seguro contra imprevistos no equipamento -->

      <!-- Seguro contra perda de renda -->
      <div v-if="verifyInsuranceIsAvailable" class="container-benefits-details">
        <div class="benefits-details-title ">
          <img class="benefits-tag" src="/icons/security-user.svg">
          <div class="mb:site:w-[240px]">
            <h3 class="pb-2 flex flex-col lg:site:gap-1">
              <span class="fonts-body-large-bold benefits-title !text-3xs !md:site:mb-0">Seguro proteção financeira</span>
              <span v-if="responseSimulationOpportunities && pointsInsuranceEquipment" class="text-brand-secondary-dark flex items-center text-micro">
                <IMaterialSymbolsWorkspacePremiumOutline class="w-[16px] h-[16px] inline mr-1.5" />
                <span><strong>+{{ pointsInsuranceEquipment[0].gained_points.toLocaleString('pt-BR') }} pontos </strong> no Solfácil Mais</span>
              </span>
            </h3>
            <p class="fonts-body-medium-regular text-brand-primary-medium">
              Protege seu cliente contra imprevistos financeiros como desemprego, perda de renda ou falecimento.
              <a
                :href="projectIsCompleted ? '#' : 'https://solfacil.com.br/seguros/?_gl=1*am5k6q*_ga*ODYzMTgzMjkyLjE2ODAwOTkxMTQ.*_ga_NQJ9ZNTL71*MTY4NDkzNjA2Mi40Ny4xLjE2ODQ5MzYwNjkuMC4wLjA'"
                class="underline fonts-body-medium-regular"
                :class="projectIsCompleted ? 'text-brand-primary-medium opacity-75 cursor-not-allowed' : 'text-brand-secondary-pure'"
                target="_blank"
              >
                Mais detalhes
              </a>
            </p>
          </div>
        </div>
        <div class="benefits-details-elements">
          <div class="min-w-[142px] flex flex-col gap-1">
            <h3 class="text-[16px] benefits-title text-neutral-low-medium">
              <strong class="!text-[16px] fonts-body-medium-bold">{{ `+ ${formatMoney(getFieldValueOfAddon('Prestamista', 'installment_price'))}` }}</strong><span class="text-[14px]">/mês</span>
            </h3>
            <p class="!text-[12px] text-neutral-low-light">
              <span class="!text-[12px] fonts-body-medium-bold">Sua comissão: </span><span>{{ formatMoney(getFieldValueOfAddon('Prestamista', 'partner_commission')) }}</span>
            </p>
          </div>
          <SolSwitch
            id="Prestamista"
            :label="getLabel('Prestamista')"
            text-direction="right"
            :disabled="projectIsCompleted || getIsLocked('Prestamista')"
            :checked="getIsApplied('Prestamista')"
            name="Prestamista"
            value="Prestamista"
            @change="toggleAddonAdapter"
          />
        </div>
      </div>
      <!-- Close Seguro contra perda de renda -->

      <!-- Banner Solfácil Mais Black -->
      <div
        v-if="showBlackFreeEquipmentInsurance"
        class="bg-brand-primary-pure mt-5 rounded-md flex items-center justify-center flex-col h-[80px] overflow-hidden w-full p-4 lg:site:px-10"
      >
        <div class="flex justify-between items-center w-full gap-2">
          <div class="rounded-md bg-[#222] flex flex-col gap-1 text-neutral-high-light p-2">
            <h2 class="text-[16px] md:site:text-[20px] text-neutral-high-light font-bold">
              Seguro <span class="text-brand-secondary-medium font-normal">grátis</span>
            </h2>
            <p class="text-[12px]">
              Isso é benefício exclusivo
            </p>
          </div>

          <div>
            <!-- Mobile -->
            <img
              class="md:site:hidden"
              src="/images/logo-solfacil-mais-black-mobile.svg"
              alt="Logo Solfácil mais black"
              title="Solfácil mais black"
            >
            <!-- Desktop -->
            <img
              class="hidden md:site:block"
              src="/images/logo-solfacil-mais-black.svg"
              alt="Logo Solfácil mais black"
              title="Solfácil mais black"
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.details-container {
  @apply flex justify-between w-full flex-col gap-6 lg:site:gap-0;
  @apply p-4xs my-6 bg-neutral-high-pure rounded-lg mb-giga;
  @screen lg:site {
    @apply p-xs;
  }

  > .container-benefits-details {
    @apply w-full my-0 flex flex-col;
    @screen lg:site {
      @apply w-full my-0 flex flex-row gap-6;
    }
    .benefits-details-title {
      @apply flex w-full mb-2xs;
      @screen lg:site {
        @apply flex w-full;
      }

      .benefits-title {
        @apply mb-nano text-stroke-brand-primary-dark;
      }

      > .benefits-tag {
        @apply text-md mr-nano mb-nano self-start max-w-[24px];
        @screen lg:site {
          @apply mx-nano;
        }
      }
    }
    .benefits-details-elements {
      @apply flex w-full self-center flex-col mt-0 gap-1 flex-row pl-8;
      @screen lg:site {
        @apply flex ml-nano text-right w-auto px-0 gap-6 lg:site:min-w-[292px];
      }
      .benefits-title {
        h3 {
          @apply ml-xs;
          @screen lg:site {
            @apply ml-0;
          }
        }
      }
      >p {
        @apply ml-xs;
        @screen lg:site {
          @apply ml-0;
        }
      }
      .sol-switch-core {
        @apply justify-end;
        @screen lg:site {
          @apply ml-0;
        }
        .label {
          @apply min-w-[70px] text-left;
        }
      }

    }
  }
}
</style>
